/**
 * Right-side contextual help drawer using Radix Dialog.
 * Displays page-specific help content with frosted professional styling.
 */

import * as Dialog from "@radix-ui/react-dialog";
import { X, BookOpen, ArrowRight } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import type { HelpPageContent } from "@/data/help-content";

interface HelpDrawerProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly content: HelpPageContent | null;
}

const ROLE_LABELS: Record<string, string> = {
  admin: "Admin",
  technician: "Technician",
  auditor: "Auditor",
};

export function HelpDrawer({ isOpen, onClose, content }: HelpDrawerProps) {
  const navigate = useNavigate();

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/30 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content className="fixed right-0 top-0 z-50 h-full w-full max-w-md border-l border-sc-border bg-sc-surface-solid shadow-xl data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right duration-300 overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-sc-border bg-sc-surface-solid">
            <div className="flex items-center gap-2">
              <BookOpen size={18} className="text-sc-green" />
              <Dialog.Title className="text-lg font-display font-semibold text-sc-navy">
                Help
              </Dialog.Title>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                className="w-8 h-8 rounded-lg flex items-center justify-center text-sc-text-muted hover:bg-sc-surface-hover transition-colors"
                aria-label="Close help"
              >
                <X size={18} />
              </button>
            </Dialog.Close>
          </div>

          {/* Content */}
          <div className="px-6 py-5 space-y-6">
            {content ? (
              <>
                {/* Title & Description */}
                <div>
                  <h2 className="text-h2 font-display font-semibold text-sc-navy mb-2">
                    {content.title}
                  </h2>
                  <p className="text-body-sm text-sc-text leading-relaxed">
                    {content.description}
                  </p>
                </div>

                {/* Roles */}
                <div className="flex gap-2">
                  {content.roles.map((role) => (
                    <span
                      key={role}
                      className="px-2 py-0.5 text-xs font-medium rounded-full bg-sc-green/10 text-sc-green border border-sc-green/20"
                    >
                      {ROLE_LABELS[role] ?? role}
                    </span>
                  ))}
                </div>

                {/* Key Concepts */}
                {content.concepts.length > 0 && (
                  <div>
                    <h3 className="text-body-sm font-semibold text-sc-navy mb-3 uppercase tracking-wider">
                      Key Concepts
                    </h3>
                    <dl className="space-y-3">
                      {content.concepts.map((concept) => (
                        <div
                          key={concept.term}
                          className="rounded-lg border border-sc-border bg-sc-surface-hover/50 p-3"
                        >
                          <dt className="text-body-sm font-semibold text-sc-navy">
                            {concept.term}
                          </dt>
                          <dd className="text-body-sm text-sc-text mt-1 leading-relaxed">
                            {concept.description}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                )}

                {/* How to Use */}
                {content.steps.length > 0 && (
                  <div>
                    <h3 className="text-body-sm font-semibold text-sc-navy mb-3 uppercase tracking-wider">
                      How to Use
                    </h3>
                    <ol className="space-y-2">
                      {content.steps.map((step, i) => (
                        <li key={i} className="flex gap-3 text-body-sm text-sc-text">
                          <span className="flex-shrink-0 w-5 h-5 rounded-full bg-sc-green/10 text-sc-green text-xs font-semibold flex items-center justify-center mt-0.5">
                            {i + 1}
                          </span>
                          <span className="leading-relaxed">{step}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}

                {/* Related Pages */}
                {content.relatedPages && content.relatedPages.length > 0 && (
                  <div>
                    <h3 className="text-body-sm font-semibold text-sc-navy mb-3 uppercase tracking-wider">
                      Related Pages
                    </h3>
                    <div className="space-y-1">
                      {content.relatedPages.map((page) => (
                        <button
                          key={page.href}
                          type="button"
                          onClick={() => {
                            void navigate({ to: page.href });
                            onClose();
                          }}
                          className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-body-sm text-sc-text hover:bg-sc-surface-hover hover:text-sc-navy transition-colors"
                        >
                          <span>{page.label}</span>
                          <ArrowRight size={14} />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Keyboard shortcut hint */}
                <div className="pt-2 border-t border-sc-border">
                  <p className="text-xs text-sc-text-muted text-center">
                    Press <kbd className="px-1.5 py-0.5 rounded bg-sc-glass border border-sc-glass-border font-mono text-xs">?</kbd> to toggle help
                  </p>
                </div>
              </>
            ) : (
              <div className="text-center py-12">
                <BookOpen size={32} className="mx-auto text-sc-text-muted/40 mb-3" />
                <p className="text-body-sm text-sc-text-muted">
                  No help content available for this page.
                </p>
              </div>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
